// src/components/TeamPairing.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { firestore, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { formatHeight, formatWeight } from '../utils/heightWeightUtils';
import { getDisplayName, getAgeFromYear, abbreviateDays } from '../utils/flyerDisplay';
import { getTeamDisplayLabel } from '../utils/teamDisplayLabel';
import './TeamPairing.css';
import './admin/FmModal.css';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';
import EmailComposerModal from './EmailComposerModal';
import { useEmailComposer } from '../hooks/useEmailComposer';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable, formatEmailList } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import {
  isMentorEligibleForPairing,
  isFmMentorPairingExcluded,
  isMentorRegistrationDeclinedForPairing,
  mentorNeedsFuryApproveForAssignment,
  registrationMentorStatusLabel,
} from '../utils/mentorEligibility';
import { getFuryRegistrationIdToken } from '../utils/furyIdentityToken';

const TEAM_TO_EMAIL = process.env.REACT_APP_EMAIL_TEAM_TO || ''; // optional: your email as To when sending "team" (BCC mentor+freshlings)

const NOTIFICATION_STATUSES = [
  { value: 'pending', label: "Don't know yet" },
  { value: 'emailed', label: 'Emailed' },
  { value: 'issue', label: 'Issue' },
];

const flyerLabel = (f) => getDisplayName(f) || f.email || '—';

/** Resolve mentor flyer name into mentorName when the team doc has no mentorName. */
function teamForDisplayLabel(team, mentorFlyer) {
  const fromFlyer = mentorFlyer ? (getDisplayName(mentorFlyer) || '').trim() : '';
  return {
    ...team,
    mentorName: team.mentorName || fromFlyer || undefined,
  };
}

const mentorEmailDone = (team) =>
  !!(team.mentorWarningShotSentAt || team.mentorWarningShotManual);
const teamIntroEmailDone = (team) =>
  !!(team.teamIntroductionSentAt || team.teamIntroductionManual);

const isTeamIncomplete = (team) => {
  const hasMentor = !!(team.mentor || '').toString().trim();
  const count = (team.freshlings || []).length;
  return !hasMentor || count < 2;
};

const isWithdrawnOrDuplicate = (flyer) => {
  const statusText = (flyer.status || '').toString().toLowerCase();
  const notesText = (flyer.notes || '').toString().toLowerCase();

  const statusHasWithdrawOrCancel =
    statusText.includes('withdrawn') ||
    statusText.includes('cancelled') ||
    statusText.includes('canceled');
  const notesHasWithdrawOrCancel =
    notesText.includes('withdrawn') ||
    notesText.includes('cancelled') ||
    notesText.includes('canceled');

  const statusHasDuplicate = statusText.includes('duplicate');
  const notesHasDuplicate = notesText.includes('duplicate');

  // For mentors, treat them as withdrawn only if the status/notes say withdrawn/cancelled.
  // A mentor who has an accepted entry but whose status/notes mention "duplicate"
  // should not be treated as cancelled here; the backend already collapses
  // duplicate+accepted rows down to a single accepted mentor.
  if (flyer?.type === 'mentor') {
    return statusHasWithdrawOrCancel || notesHasWithdrawOrCancel;
  }

  return (
    statusHasWithdrawOrCancel ||
    notesHasWithdrawOrCancel ||
    statusHasDuplicate ||
    notesHasDuplicate
  );
};

/** True if flyer is cancelled (boolean) or withdrawn/duplicate (status/notes). */
const isCancelledOrWithdrawn = (flyer) => {
  if (!flyer) return false;
  return !!(flyer.cancelled === true || isWithdrawnOrDuplicate(flyer));
};

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const TEAM_PAIRING_EXPORT_COLS = [
  { id: 'teamNumber', label: 'Team #', default: true },
  { id: 'mentor', label: 'Mentor', default: true },
  { id: 'mentorEmail', label: 'Mentor email', default: false },
  { id: 'freshlings', label: 'Freshlings', default: true },
  { id: 'status', label: 'Status', default: true },
];

function TeamPairing() {
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('eventId') || '';
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [flyers, setFlyers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tab state: 'pairing' | 'teams'
  const [activeTab, setActiveTab] = useState('pairing');
  const [teamsCompactView, setTeamsCompactView] = useState(false);
  const [teamsViewSearch, setTeamsViewSearch] = useState('');
  const [teamsFilterIssue, setTeamsFilterIssue] = useState('all'); // all | only | hide
  const [teamsFilterIncomplete, setTeamsFilterIncomplete] = useState('all'); // all | only | complete
  const [teamsFilterMentorEmail, setTeamsFilterMentorEmail] = useState('all'); // all | not_sent | sent
  const [teamsFilterTeamEmail, setTeamsFilterTeamEmail] = useState('all'); // all | not_sent | sent
  const [teamsSortBy, setTeamsSortBy] = useState('default'); // default | teamNumber | mentor | status | mentorEmail | teamEmail
  const [teamsSortDir, setTeamsSortDir] = useState('asc'); // asc | desc
  /** When jumping from compact view, keep this id in the detail grid even if filters hide it, until scroll/highlight finishes. */
  const [scrollFocusTeamId, setScrollFocusTeamId] = useState(null);
  const [highlightTeamId, setHighlightTeamId] = useState(null);
  const teamCardRefs = useRef({});

  // Filters for unassigned list
  const [nameSearch, setNameSearch] = useState('');
  const [sortUnassignedBy, setSortUnassignedBy] = useState('teammates'); // teammates | alpha | sheetOrder | ht | wt | wt-ht | intensity | jumps | days
  const [filterHasTeammates, setFilterHasTeammates] = useState('all'); // all | yes | no

  // Form team: selected mentor + freshlings
  const [selectedMentorEmail, setSelectedMentorEmail] = useState('');
  const [selectedFreshlingEmails, setSelectedFreshlingEmails] = useState([]);
  const [nextTeamNumber, setNextTeamNumber] = useState('');
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [newPasscode, setNewPasscode] = useState('');
  const [creating, setCreating] = useState(false);
  const [furyApprovingMentor, setFuryApprovingMentor] = useState(false);
  const [cleaningUnused, setCleaningUnused] = useState(false);
  const [notesModalFlyer, setNotesModalFlyer] = useState(null);
  const [notesEditValue, setNotesEditValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const { emailComposerProps, openEmailComposer, closeEmailComposer } = useEmailComposer();
  const [assigningTeamNumbers, setAssigningTeamNumbers] = useState(false);
  const [clearingTeamNumbers, setClearingTeamNumbers] = useState(false);
  const [pairingTableExportMode, setPairingTableExportMode] = useState(null); // 'print' | 'copy' | null
  const [pairingExportCols, setPairingExportCols] = useState(() => new Set());
  const [emailCopyFeedbackKey, setEmailCopyFeedbackKey] = useState(null);
  const emailCopyFeedbackTimerRef = useRef(null);

  useModalEscapeKey(!!notesModalFlyer, () => setNotesModalFlyer(null));

  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setActiveEvents(data);
        if (data.length) {
          setSelectedEvent(
            resolveAdminDefaultEventId({
              eventIdParam: eventIdParam,
              activeEventId: activeId,
              events: data,
            })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch events: ' + err.message);
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [eventIdParam]);

  useEffect(() => {
    if (!selectedEvent) return;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const [flyersSnap, teamsSnap] = await Promise.all([
          getDocs(query(collection(firestore, 'flyers'), where('eventId', '==', selectedEvent))),
          getDocs(query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent))),
        ]);
        setFlyers(flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedEvent]);

  useEffect(
    () => () => {
      if (emailCopyFeedbackTimerRef.current) {
        clearTimeout(emailCopyFeedbackTimerRef.current);
      }
    },
    []
  );

  const mentors = useMemo(() => flyers.filter((f) => f.type === 'mentor'), [flyers]);
  const freshlings = useMemo(() => flyers.filter((f) => f.type === 'freshling'), [flyers]);
  const selectedEventObj = useMemo(
    () => activeEvents.find((e) => e.id === selectedEvent) || null,
    [activeEvents, selectedEvent]
  );

  const assignedMentorEmails = useMemo(
    () => new Set(teams.map((t) => (t.mentor || '').toLowerCase()).filter(Boolean)),
    [teams]
  );
  const assignedFreshlingEmails = useMemo(() => {
    const set = new Set();
    teams.forEach((t) => {
      (t.freshlings || []).forEach((e) => set.add((e || '').toLowerCase()));
    });
    return set;
  }, [teams]);

  const unassignedMentors = useMemo(
    () => mentors.filter(
      (m) =>
        !m.cancelled &&
        !m.waiting &&
        !isWithdrawnOrDuplicate(m) &&
        !isFmMentorPairingExcluded(m) &&
        !assignedMentorEmails.has((m.email || '').toLowerCase())
    ),
    [mentors, assignedMentorEmails]
  );

  const excludedUnassignedMentors = useMemo(
    () =>
      mentors.filter(
        (m) =>
          isFmMentorPairingExcluded(m) &&
          !m.cancelled &&
          !m.waiting &&
          !assignedMentorEmails.has((m.email || '').toLowerCase())
      ),
    [mentors, assignedMentorEmails]
  );

  const waitingMentors = useMemo(
    () => mentors.filter(
      (m) => !m.cancelled && !assignedMentorEmails.has((m.email || '').toLowerCase()) && !!m.waiting
    ),
    [mentors, assignedMentorEmails]
  );

  const unassignedFreshlings = useMemo(
    () => freshlings.filter(
      (f) =>
        !f.cancelled &&
        !isWithdrawnOrDuplicate(f) &&
        !assignedFreshlingEmails.has((f.email || '').toLowerCase())
    ),
    [freshlings, assignedFreshlingEmails]
  );

  /** Mentors that count toward “everyone paired” (excludes waiting/cancelled/withdrawn/FM-excluded/registration declined). */
  const mentorsInPairingPoolTotal = useMemo(
    () =>
      mentors.filter(
        (m) =>
          !m.cancelled &&
          !m.waiting &&
          !isWithdrawnOrDuplicate(m) &&
          !isFmMentorPairingExcluded(m) &&
          !isMentorRegistrationDeclinedForPairing(m)
      ).length,
    [mentors]
  );
  const pairableFreshlingCount = useMemo(
    () => freshlings.filter((f) => !f.cancelled && !isWithdrawnOrDuplicate(f)).length,
    [freshlings]
  );

  const unassignedMentorsInPairingQueue = useMemo(
    () => unassignedMentors.filter((m) => !isMentorRegistrationDeclinedForPairing(m)),
    [unassignedMentors]
  );

  const pairingUnassignedQueuesEmpty =
    unassignedMentorsInPairingQueue.length === 0 && unassignedFreshlings.length === 0;

  const hasTeammates = (f) => {
    const t = (f.teammates || f.team || '').toString().trim();
    return t.length > 0 && t.toLowerCase() !== 'n/a' && t.toLowerCase() !== 'na';
  };

  const matchesNameSearch = (flyer, search) => {
    if (!search || !search.trim()) return true;
    const q = search.trim().toLowerCase();
    const name = (getDisplayName(flyer) || '').toLowerCase();
    const email = (flyer.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  };

  const filteredMentorsBySearch = useMemo(
    () =>
      [...unassignedMentors]
        .filter((m) => matchesNameSearch(m, nameSearch))
        .sort((a, b) => {
          const da = isMentorRegistrationDeclinedForPairing(a) ? 1 : 0;
          const db = isMentorRegistrationDeclinedForPairing(b) ? 1 : 0;
          if (da !== db) return da - db;
          return (getDisplayName(a) || a.email || '').localeCompare(
            getDisplayName(b) || b.email || '',
            undefined,
            { sensitivity: 'base' }
          );
        }),
    [unassignedMentors, nameSearch]
  );

  const filteredSortedFreshlings = useMemo(() => {
    let list = unassignedFreshlings.filter((f) => matchesNameSearch(f, nameSearch));
    if (filterHasTeammates === 'yes') list = list.filter(hasTeammates);
    if (filterHasTeammates === 'no') list = list.filter((f) => !hasTeammates(f));
    const jumpNum = (f) => {
      const n = parseInt(f.jumpNumber, 10);
      return Number.isFinite(n) ? n : -1;
    };
    list = [...list].sort((a, b) => {
      switch (sortUnassignedBy) {
        case 'teammates': {
          const va = hasTeammates(a) ? 1 : 0;
          const vb = hasTeammates(b) ? 1 : 0;
          if (vb !== va) return vb - va;
          return getDisplayName(a).localeCompare(getDisplayName(b), undefined, { sensitivity: 'base' });
        }
        case 'alpha':
          return getDisplayName(a).localeCompare(getDisplayName(b), undefined, { sensitivity: 'base' });
        case 'sheetOrder': {
          const parseOrder = (f) => {
            const raw = (f.sheetOrder || '').toString().trim().toLowerCase();
            if (!raw || raw === 'x') return Number.POSITIVE_INFINITY;
            const n = parseInt(raw, 10);
            return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY - 1;
          };
          return parseOrder(a) - parseOrder(b);
        }
        case 'ht': {
          const parseHt = (x) => {
            const s = (x.ht || '').toString();
            const cm = s.match(/(\d+(?:\.\d+)?)\s*cm/i);
            if (cm) return parseFloat(cm[1], 10);
            const ftIn = s.match(/(\d+)\s*'\s*(\d+)/i);
            if (ftIn) return parseInt(ftIn[1], 10) * 12 + parseInt(ftIn[2], 10);
            return 0;
          };
          return parseHt(b) - parseHt(a);
        }
        case 'wt': {
          const parseWt = (x) => {
            const s = (x.wt || '').toString();
            const m = s.match(/(\d+(?:\.\d+)?)\s*kg/i) || s.match(/(\d+(?:\.\d+)?)/);
            if (m) return parseFloat(m[1], 10);
            return 0;
          };
          return parseWt(b) - parseWt(a);
        }
        case 'wt-ht': {
          const parseWt = (x) => {
            const s = (x.wt || '').toString();
            const m = s.match(/(\d+(?:\.\d+)?)\s*kg/i) || s.match(/(\d+(?:\.\d+)?)/);
            if (m) return parseFloat(m[1], 10);
            return 0;
          };
          const parseHt = (x) => {
            const s = (x.ht || '').toString();
            const cm = s.match(/(\d+(?:\.\d+)?)\s*cm/i);
            if (cm) return parseFloat(cm[1], 10);
            const ftIn = s.match(/(\d+)\s*'\s*(\d+)/i);
            if (ftIn) return parseInt(ftIn[1], 10) * 12 + parseInt(ftIn[2], 10);
            return 0;
          };
          const w = parseWt(b) - parseWt(a);
          if (w !== 0) return w;
          return parseHt(b) - parseHt(a);
        }
        case 'intensity':
          return (b.intensity || '').localeCompare(a.intensity || '', undefined, { sensitivity: 'base' });
        case 'jumps':
          return jumpNum(b) - jumpNum(a);
        case 'days':
          return (b.days || '').localeCompare(a.days || '', undefined, { sensitivity: 'base' });
        default:
          return getDisplayName(a).localeCompare(getDisplayName(b), undefined, { sensitivity: 'base' });
      }
    });
    return list;
  }, [unassignedFreshlings, nameSearch, filterHasTeammates, sortUnassignedBy]);

  const suggestedNextTeamNumber = useMemo(() => {
    const nums = teams
      .map((t) => parseInt(t.teamNumber, 10))
      .filter((n) => Number.isFinite(n));
    return nums.length ? String(Math.max(...nums) + 1) : '1';
  }, [teams]);

  const teamHasWithdrawnOrDuplicate = useCallback(
    (team) => {
      const mentorFlyer = mentors.find((m) => (m.email || '').toLowerCase() === (team.mentor || '').toLowerCase());
      if (mentorFlyer && isWithdrawnOrDuplicate(mentorFlyer)) return true;
      return (team.freshlings || []).some((email) => {
        const f = freshlings.find((fl) => (fl.email || '').toLowerCase() === (email || '').toLowerCase());
        return f && isWithdrawnOrDuplicate(f);
      });
    },
    [mentors, freshlings]
  );

  const teamHasMentorPairingPolicyIssue = useCallback(
    (team) => {
      const m = mentors.find((x) => (x.email || '').toLowerCase() === (team.mentor || '').toLowerCase());
      if (!m) return false;
      if (m.cancelled || m.waiting || isWithdrawnOrDuplicate(m)) return false;
      return isFmMentorPairingExcluded(m) || isMentorRegistrationDeclinedForPairing(m);
    },
    [mentors]
  );

  const sortedTeams = useMemo(
    () =>
      [...teams].sort((a, b) => {
        const aWarn = teamHasWithdrawnOrDuplicate(a) || teamHasMentorPairingPolicyIssue(a) ? 1 : 0;
        const bWarn = teamHasWithdrawnOrDuplicate(b) || teamHasMentorPairingPolicyIssue(b) ? 1 : 0;
        if (aWarn !== bWarn) return bWarn - aWarn; // teams with withdrawn/duplicate first
        const aNum = parseInt(a.teamNumber, 10);
        const bNum = parseInt(b.teamNumber, 10);
        const aHasNum = Number.isFinite(aNum);
        const bHasNum = Number.isFinite(bNum);
        if (aHasNum && bHasNum) return aNum - bNum;
        if (aHasNum && !bHasNum) return -1; // numbered teams before unnumbered
        if (!aHasNum && bHasNum) return 1;
        return String(a.teamNumber || '').localeCompare(String(b.teamNumber || ''));
      }),
    [teams, teamHasWithdrawnOrDuplicate, teamHasMentorPairingPolicyIssue]
  );

  const teamMatchesSearch = useCallback((team, q, mentorsList, freshlingsList) => {
    if (!q) return true;
    const mentorFlyer = team.mentor
      ? mentorsList.find((m) => (m.email || '').toLowerCase() === (team.mentor || '').toLowerCase())
      : null;
    if (mentorFlyer) {
      const name = (getDisplayName(mentorFlyer) || '').toLowerCase();
      const email = (mentorFlyer.email || '').toLowerCase();
      if (name.includes(q) || email.includes(q)) return true;
    } else if (team.mentor && (team.mentor || '').toLowerCase().includes(q)) {
      return true;
    }
    const freshlingEmails = team.freshlings || [];
    for (const email of freshlingEmails) {
      const freshlingFlyer = freshlingsList.find(
        (f) => (f.email || '').toLowerCase() === (email || '').toLowerCase()
      );
      const name = freshlingFlyer
        ? (getDisplayName(freshlingFlyer) || '').toLowerCase()
        : '';
      const em = (email || '').toLowerCase();
      if (name.includes(q) || em.includes(q)) return true;
    }
    return false;
  }, []);

  const teamsMatchingSearch = useMemo(() => {
    const q = (teamsViewSearch || '').trim().toLowerCase();
    if (!q) return sortedTeams;
    return sortedTeams.filter((t) => teamMatchesSearch(t, q, mentors, freshlings));
  }, [sortedTeams, teamsViewSearch, teamMatchesSearch, mentors, freshlings]);

  const filteredSortedTeams = useMemo(() => {
    let list = teamsMatchingSearch;

    if (teamsFilterIssue === 'only') {
      list = list.filter((t) => (t.notificationStatus || '') === 'issue');
    } else if (teamsFilterIssue === 'hide') {
      list = list.filter((t) => (t.notificationStatus || '') !== 'issue');
    }

    if (teamsFilterIncomplete === 'only') {
      list = list.filter((t) => isTeamIncomplete(t));
    } else if (teamsFilterIncomplete === 'complete') {
      list = list.filter((t) => !isTeamIncomplete(t));
    }

    if (teamsFilterMentorEmail === 'not_sent') {
      list = list.filter((t) => !mentorEmailDone(t));
    } else if (teamsFilterMentorEmail === 'sent') {
      list = list.filter((t) => mentorEmailDone(t));
    }

    if (teamsFilterTeamEmail === 'not_sent') {
      list = list.filter((t) => !teamIntroEmailDone(t));
    } else if (teamsFilterTeamEmail === 'sent') {
      list = list.filter((t) => teamIntroEmailDone(t));
    }

    if (teamsSortBy === 'default') {
      return list;
    }

    const dir = teamsSortDir === 'desc' ? -1 : 1;

    const cmpTeamNumber = (a, b) => {
      const aNum = parseInt(a.teamNumber, 10);
      const bNum = parseInt(b.teamNumber, 10);
      const aOk = Number.isFinite(aNum);
      const bOk = Number.isFinite(bNum);
      if (aOk && bOk && aNum !== bNum) return aNum - bNum;
      if (aOk && !bOk) return -1;
      if (!aOk && bOk) return 1;
      return String(a.teamNumber || '').localeCompare(String(b.teamNumber || ''), undefined, {
        sensitivity: 'base',
      });
    };

    const statusOrder = (s) => {
      const v = (s || 'pending').toLowerCase();
      if (v === 'issue') return 0;
      if (v === 'pending') return 1;
      if (v === 'emailed') return 2;
      return 3;
    };

    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (teamsSortBy) {
        case 'teamNumber':
          cmp = cmpTeamNumber(a, b);
          break;
        case 'mentor': {
          const ma = a.mentor
            ? mentors.find((m) => (m.email || '').toLowerCase() === (a.mentor || '').toLowerCase())
            : null;
          const mb = b.mentor
            ? mentors.find((m) => (m.email || '').toLowerCase() === (b.mentor || '').toLowerCase())
            : null;
          const na = (ma ? getDisplayName(ma) || ma.email : a.mentor || '').toLowerCase();
          const nb = (mb ? getDisplayName(mb) || mb.email : b.mentor || '').toLowerCase();
          cmp = na.localeCompare(nb, undefined, { sensitivity: 'base' });
          break;
        }
        case 'status':
          cmp = statusOrder(a.notificationStatus) - statusOrder(b.notificationStatus);
          break;
        case 'mentorEmail': {
          const va = mentorEmailDone(a) ? 1 : 0;
          const vb = mentorEmailDone(b) ? 1 : 0;
          cmp = va - vb;
          break;
        }
        case 'teamEmail': {
          const va = teamIntroEmailDone(a) ? 1 : 0;
          const vb = teamIntroEmailDone(b) ? 1 : 0;
          cmp = va - vb;
          break;
        }
        default:
          return 0;
      }
      if (cmp !== 0) return cmp * dir;
      return cmpTeamNumber(a, b);
    });
  }, [
    teamsMatchingSearch,
    teamsFilterIssue,
    teamsFilterIncomplete,
    teamsFilterMentorEmail,
    teamsFilterTeamEmail,
    teamsSortBy,
    teamsSortDir,
    mentors,
  ]);

  const teamsForDetailCards = useMemo(() => {
    if (!scrollFocusTeamId) return filteredSortedTeams;
    if (filteredSortedTeams.some((t) => t.id === scrollFocusTeamId)) return filteredSortedTeams;
    const extra = sortedTeams.find((t) => t.id === scrollFocusTeamId);
    return extra ? [...filteredSortedTeams, extra] : filteredSortedTeams;
  }, [filteredSortedTeams, sortedTeams, scrollFocusTeamId]);

  useEffect(() => {
    if (teamsCompactView || !scrollFocusTeamId) return;
    const id = scrollFocusTeamId;
    let timeoutId;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = teamCardRefs.current[id];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightTeamId(id);
          timeoutId = window.setTimeout(() => {
            setHighlightTeamId(null);
            setScrollFocusTeamId(null);
          }, 2200);
        } else {
          setScrollFocusTeamId(null);
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [teamsCompactView, scrollFocusTeamId]);

  const pairingTableRowRecords = useMemo(
    () =>
      filteredSortedTeams.map((team) => {
        const mentorFlyer = team.mentor
          ? mentors.find((m) => (m.email || '').toLowerCase() === (team.mentor || '').toLowerCase())
          : null;
        const freshlingFlyers = (team.freshlings || []).map(
          (email) =>
            freshlings.find((f) => (f.email || '').toLowerCase() === (email || '').toLowerCase()) || {
              email,
            }
        );
        const mentorName = mentorFlyer ? flyerLabel(mentorFlyer) : '—';
        const freshlingNames = freshlingFlyers.length
          ? freshlingFlyers.map((f) => flyerLabel(f)).join(', ')
          : '—';
        return {
          teamNumber: team.teamNumber ? `#${team.teamNumber}` : '',
          mentor: mentorName,
          mentorEmail: (team.mentor || '').trim(),
          freshlings: freshlingNames,
          status: team.notificationStatus || 'pending',
        };
      }),
    [filteredSortedTeams, mentors, freshlings]
  );

  useEffect(() => {
    if (!pairingTableExportMode) return;
    setPairingExportCols(
      new Set(TEAM_PAIRING_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  }, [pairingTableExportMode]);

  const openPairingTableExport = (mode) => {
    if (!pairingTableRowRecords.length) {
      window.alert('No teams to export for the current filter.');
      return;
    }
    setPairingTableExportMode(mode);
  };

  const resetPairingExportCols = () => {
    setPairingExportCols(
      new Set(TEAM_PAIRING_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  };

  const runPairingTableExport = (selectedColumnIds) => {
    if (!pairingTableExportMode) return;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : pairingExportCols;
    const active = TEAM_PAIRING_EXPORT_COLS.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = pairingTableRowRecords.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const eventTitle = selectedEventObj ? selectedEventObj.title || '' : '';
    const sub = 'Teams';
    if (pairingTableExportMode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const title = eventTitle || 'Team pairing';
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(title)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} team(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${title} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setPairingTableExportMode(null);
  };

  const copyAllPairedMentorEmails = () => {
    const emails = Array.from(
      new Set(teams.map((t) => (t.mentor || '').toString().trim()).filter(Boolean))
    );
    if (!emails.length) {
      window.alert('No paired mentors with email addresses to copy.');
      return;
    }
    copyText(formatEmailList(emails, 'comma'));
  };

  const handleCreateTeam = async () => {
    const hasMentor = !!selectedMentorEmail;
    const freshlingCount = selectedFreshlingEmails.length;
    if (!selectedEvent || (!hasMentor && freshlingCount === 0)) {
      alert('Select at least one member (mentor and/or freshlings) to form a team.');
      return;
    }
    const teamNum = (nextTeamNumber || '').trim();
    setCreating(true);
    setError('');
    try {
      const payload = {
        eventId: selectedEvent,
        // teamNumber is optional during pairing; leave blank/null until load order is finalized
        teamNumber: teamNum || null,
        mentor: selectedMentorEmail || '',
        freshlings: selectedFreshlingEmails,
        passcode: (newPasscode || '').trim(),
      };

      if (editingTeamId) {
        await updateDoc(doc(firestore, 'teams', editingTeamId), payload);
      } else {
        await addDoc(collection(firestore, 'teams'), {
          ...payload,
          teamName: '',
          division: '',
          guest: false,
          test: false,
          notificationStatus: 'pending',
        });
      }

      setSelectedMentorEmail('');
      setSelectedFreshlingEmails([]);
      setNextTeamNumber('');
      setNewPasscode('');
      setEditingTeamId(null);
      const q = query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent));
      const snap = await getDocs(q);
      setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(err.message || 'Failed to save team');
    } finally {
      setCreating(false);
    }
  };

  const invokeRequestFuryMentorApprove = useCallback(async (flyer) => {
    if (!flyer || !flyer.id || !selectedEvent) {
      throw new Error('Missing mentor or event.');
    }
    const tok = await getFuryRegistrationIdToken();
    if (!tok.ok) {
      throw new Error(tok.error || 'Could not get Fury ID token.');
    }
    const fn = httpsCallable(functions, 'requestFuryMentorApprove');
    await fn({ eventId: selectedEvent, flyerId: flyer.id, idToken: tok.idToken });
  }, [selectedEvent]);

  const refreshEventFlyers = useCallback(async () => {
    const flyersSnap = await getDocs(
      query(collection(firestore, 'flyers'), where('eventId', '==', selectedEvent))
    );
    setFlyers(flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, [selectedEvent]);

  const handleRequestFuryMentorApprove = useCallback(async () => {
    const m = mentors.find((x) => (x.email || '').toLowerCase() === (selectedMentorEmail || '').toLowerCase());
    if (!m || !m.id || !selectedEvent) return;
    if (!mentorNeedsFuryApproveForAssignment(m)) return;
    if (selectedEventObj?.registrationImportSource !== 'fury_api') {
      window.alert('Fury approve is only available when the event uses Fury API registration import.');
      return;
    }
    setFuryApprovingMentor(true);
    setError('');
    try {
      await invokeRequestFuryMentorApprove(m);
      await refreshEventFlyers();
      window.alert(
        'Request sent to Fury Registration. Run Import all (Fury) or wait for the next sync so this mentor shows as approved here.'
      );
    } catch (err) {
      const msg = err.message || 'Fury approve failed';
      setError(msg);
      window.alert(msg);
    } finally {
      setFuryApprovingMentor(false);
    }
  }, [
    mentors,
    selectedMentorEmail,
    selectedEvent,
    selectedEventObj,
    invokeRequestFuryMentorApprove,
    refreshEventFlyers,
  ]);

  const handleAssignTeamNumbersToBlanks = async () => {
    if (!selectedEvent || !teams.length || assigningTeamNumbers) return;
    const blanks = sortedTeams.filter((t) => !String(t.teamNumber || '').trim());
    if (!blanks.length) {
      window.alert('No teams with blank numbers to assign.');
      return;
    }
    const nums = teams
      .map((t) => parseInt(t.teamNumber, 10))
      .filter((n) => Number.isFinite(n));
    let next = nums.length ? Math.max(...nums) + 1 : 1;
    setAssigningTeamNumbers(true);
    try {
      for (const team of blanks) {
        const value = String(next++);
        await updateDoc(doc(firestore, 'teams', team.id), { teamNumber: value });
      }
      const snap = await getDocs(
        query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent))
      );
      setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(err.message || 'Failed to assign team numbers.');
    } finally {
      setAssigningTeamNumbers(false);
    }
  };

  const handleClearAllTeamNumbers = async () => {
    if (!selectedEvent || !teams.length || clearingTeamNumbers) return;
    if (!window.confirm('Clear public team numbers for all teams in this event?')) return;
    setClearingTeamNumbers(true);
    try {
      for (const team of teams) {
        if (team.teamNumber) {
          await updateDoc(doc(firestore, 'teams', team.id), { teamNumber: null });
        }
      }
      const snap = await getDocs(
        query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent))
      );
      setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(err.message || 'Failed to clear team numbers.');
    } finally {
      setClearingTeamNumbers(false);
    }
  };

  const handleInlineTeamNumberChange = async (teamId, rawValue) => {
    const value = (rawValue || '').trim();
    const newNumber = value || null;
    try {
      await updateDoc(doc(firestore, 'teams', teamId), { teamNumber: newNumber });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, teamNumber: newNumber } : t))
      );
    } catch (err) {
      setError(err.message || 'Failed to update team number.');
    }
  };

  const handleNotificationStatusChange = async (teamId, status) => {
    try {
      await updateDoc(doc(firestore, 'teams', teamId), { notificationStatus: status });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, notificationStatus: status } : t))
      );
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  const toggleMentorWarningShotManual = async (team) => {
    const teamId = team.id;
    const current = !!team.mentorWarningShotManual;
    try {
      await updateDoc(doc(firestore, 'teams', teamId), {
        mentorWarningShotManual: !current,
      });
      setTeams((prev) =>
        prev.map((t) =>
          t.id === teamId ? { ...t, mentorWarningShotManual: !current } : t
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update mentor warning shot flag');
    }
  };

  const toggleTeamIntroductionManual = async (team) => {
    const teamId = team.id;
    const current = !!team.teamIntroductionManual;
    try {
      const updates = current
        ? { teamIntroductionManual: false, teamIntroductionSentAt: null }
        : { teamIntroductionManual: true };
      await updateDoc(doc(firestore, 'teams', teamId), updates);
      setTeams((prev) =>
        prev.map((t) =>
          t.id === teamId
            ? { ...t, ...updates }
            : t
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update team email flag');
    }
  };

  const handleRemoveTeam = async (teamId) => {
    if (!window.confirm('Remove this team? Members will show as unassigned again.')) return;
    try {
      await deleteDoc(doc(firestore, 'teams', teamId));
      const snap = await getDocs(
        query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent))
      );
      setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(err.message || 'Failed to remove team');
    }
  };

  const handleRemoveMentorFromTeam = async (teamId) => {
    try {
      await updateDoc(doc(firestore, 'teams', teamId), { mentor: '' });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, mentor: '' } : t))
      );
    } catch (err) {
      setError(err.message || 'Failed to remove mentor');
    }
  };

  const handleRemoveFreshlingFromTeam = async (teamId, email) => {
    if (!email) return;
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    const current = (team.freshlings || []).filter(Boolean);
    const updated = current.filter((e) => e !== email);
    try {
      await updateDoc(doc(firestore, 'teams', teamId), { freshlings: updated });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, freshlings: updated } : t))
      );
    } catch (err) {
      setError(err.message || 'Failed to remove freshling');
    }
  };

  const handleAddMentorToTeam = async (teamId, mentorEmail) => {
    if (!mentorEmail) return;
    const flyer = mentors.find((m) => (m.email || '').toLowerCase() === mentorEmail.toLowerCase());
    if (flyer && !isMentorEligibleForPairing(flyer)) {
      setError('That mentor is not staff-approved or is declined — cannot add to a team.');
      return;
    }
    try {
      await updateDoc(doc(firestore, 'teams', teamId), { mentor: mentorEmail });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, mentor: mentorEmail } : t))
      );
    } catch (err) {
      setError(err.message || 'Failed to add mentor');
    }
  };

  const handleAddFreshlingToTeam = async (teamId, freshlingEmail) => {
    if (!freshlingEmail) return;
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    const current = (team.freshlings || []).filter(Boolean);
    if (current.includes(freshlingEmail)) return;
    const updated = [...current, freshlingEmail];
    try {
      await updateDoc(doc(firestore, 'teams', teamId), { freshlings: updated });
      setTeams((prev) =>
        prev.map((t) => (t.id === teamId ? { ...t, freshlings: updated } : t))
      );
    } catch (err) {
      setError(err.message || 'Failed to add freshling');
    }
  };

  const isEmptyTeam = (t) =>
    !(t.mentor || '').toString().trim() && (!t.freshlings || t.freshlings.length === 0);

  const handleRemoveUnusedFromTeams = async () => {
    if (!selectedEvent) return;
    const validEmails = new Set(flyers.map((f) => (f.email || '').toLowerCase().trim()).filter(Boolean));
    const toUpdate = teams.filter((t) => {
      const mentorOk = !t.mentor || validEmails.has((t.mentor || '').toLowerCase().trim());
      const freshlingsList = t.freshlings || [];
      const validFreshlings = freshlingsList.filter((e) => validEmails.has((e || '').toLowerCase().trim()));
      return !mentorOk || validFreshlings.length !== freshlingsList.length;
    });
    const alreadyEmpty = teams.filter(isEmptyTeam);
    const toDelete = new Set(alreadyEmpty.map((t) => t.id));
    if (toUpdate.length === 0 && toDelete.size === 0) {
      setError('');
      alert('All team members are current flyers. No empty teams. Nothing to clean.');
      return;
    }
    const msg = toDelete.size > 0 && toUpdate.length > 0
      ? `Remove references to deleted flyers from ${toUpdate.length} team(s) and delete ${toDelete.size} empty team(s)?`
      : toDelete.size > 0
        ? `Delete ${toDelete.size} empty team(s)?`
        : `Remove references to deleted flyers from ${toUpdate.length} team(s)? Mentors or freshlings not in the current flyer list will be cleared.`;
    if (!window.confirm(msg)) return;
    setCleaningUnused(true);
    setError('');
    try {
      for (const t of teams) {
        if (toDelete.has(t.id)) {
          await deleteDoc(doc(firestore, 'teams', t.id));
          continue;
        }
        if (!toUpdate.find((u) => u.id === t.id)) continue;
        const mentorOk = validEmails.has((t.mentor || '').toLowerCase().trim());
        const freshlingsList = t.freshlings || [];
        const validFreshlings = freshlingsList.filter((e) => validEmails.has((e || '').toLowerCase().trim()));
        if (!mentorOk && validFreshlings.length === 0) {
          await deleteDoc(doc(firestore, 'teams', t.id));
        } else {
          await updateDoc(doc(firestore, 'teams', t.id), {
            mentor: mentorOk ? t.mentor : '',
            freshlings: validFreshlings,
          });
        }
      }
      const snap = await getDocs(query(collection(firestore, 'teams'), where('eventId', '==', selectedEvent)));
      setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      const becameEmpty = toUpdate.filter((t) => {
        const mentorOk = validEmails.has((t.mentor || '').toLowerCase().trim());
        const validFreshlings = (t.freshlings || []).filter((e) => validEmails.has((e || '').toLowerCase().trim()));
        return !mentorOk && validFreshlings.length === 0;
      }).length;
      const totalDeleted = toDelete.size + becameEmpty;
      alert(totalDeleted > 0
        ? `Cleaned teams. Removed references to deleted flyers. Deleted ${totalDeleted} empty team(s).`
        : `Cleaned ${toUpdate.length} team(s). Removed references to flyers no longer in the event.`);
    } catch (err) {
      setError(err.message || 'Failed to remove unused flyers from teams');
    } finally {
      setCleaningUnused(false);
    }
  };

  const toggleFreshlingSelection = (email) => {
    setSelectedFreshlingEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : prev.length >= 2 ? prev : [...prev, email]
    );
  };

  const copyEmailToClipboard = useCallback(async (event, email, feedbackKey) => {
    event.stopPropagation();
    if (!email) return;
    if (emailCopyFeedbackTimerRef.current) {
      clearTimeout(emailCopyFeedbackTimerRef.current);
      emailCopyFeedbackTimerRef.current = null;
    }
    await copyText(email, {
      onSuccess: () => {
        if (!feedbackKey) return;
        setEmailCopyFeedbackKey(feedbackKey);
        emailCopyFeedbackTimerRef.current = setTimeout(() => {
          setEmailCopyFeedbackKey((current) => (current === feedbackKey ? null : current));
          emailCopyFeedbackTimerRef.current = null;
        }, 1500);
      },
    });
  }, []);

  const selectedMentorFlyer = useMemo(
    () =>
      mentors.find(
        (m) => (m.email || '').toLowerCase() === (selectedMentorEmail || '').toLowerCase()
      ) || null,
    [mentors, selectedMentorEmail]
  );

  const handleEmailSent = async ({ selectedTemplate, context: emailContext }) => {
    if (!emailContext) return;
    const teamId = emailContext.teamId;
    try {
      if (teamId && emailContext.markMentorWarningSent && selectedTemplate === 'mentor_warning') {
        await updateDoc(doc(firestore, 'teams', teamId), {
          mentorWarningShotSentAt: serverTimestamp(),
        });
        setTeams((prev) =>
          prev.map((t) =>
            t.id === teamId
              ? { ...t, mentorWarningShotSentAt: new Date() }
              : t
          )
        );
      }
      if (teamId && emailContext.markTeamIntroSent && selectedTemplate === 'team_intro') {
        await updateDoc(doc(firestore, 'teams', teamId), {
          teamIntroductionSentAt: serverTimestamp(),
        });
        setTeams((prev) =>
          prev.map((t) =>
            t.id === teamId
              ? { ...t, teamIntroductionSentAt: new Date() }
              : t
          )
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to update team after email.');
    }

    if (selectedTemplate !== 'mentor_warning') return;
    if (selectedEventObj?.registrationImportSource !== 'fury_api' || !selectedEvent) return;

    let mentorFlyer = null;
    if (emailContext.flyer && emailContext.flyer.type === 'mentor') {
      mentorFlyer = mentors.find((m) => m.id === emailContext.flyer.id) || emailContext.flyer;
    } else if (emailContext.team) {
      const mentorEmail = (emailContext.team.mentor || '').trim().toLowerCase();
      if (mentorEmail) {
        mentorFlyer = mentors.find((m) => (m.email || '').trim().toLowerCase() === mentorEmail) || null;
      }
    }
    if (!mentorFlyer || !mentorNeedsFuryApproveForAssignment(mentorFlyer)) return;

    try {
      await invokeRequestFuryMentorApprove(mentorFlyer);
      await refreshEventFlyers();
    } catch (err) {
      const msg = err.message || 'Fury mentor approve failed';
      setError(msg);
      window.alert(
        'The mentor warning email was sent, but approving this mentor in Fury Registration failed. ' +
          'Use “Request mentor approve (Fury)” after fixing the issue, or approve in Fury directly.\n\n' +
          msg
      );
    }
  };

  const openNotesModal = (flyer, e) => {
    if (e) e.stopPropagation();
    setNotesModalFlyer(flyer);
    setNotesEditValue(flyer.notes || '');
  };

  const saveNotes = async () => {
    if (!notesModalFlyer) return;
    setSavingNotes(true);
    try {
      const val = (notesEditValue || '').trim() || null;
      await updateDoc(doc(firestore, 'flyers', notesModalFlyer.id), { notes: val });
      setFlyers((prev) =>
        prev.map((f) => (f.id === notesModalFlyer.id ? { ...f, notes: val } : f))
      );
      setNotesModalFlyer(null);
    } catch (err) {
      setError(err.message || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading && !flyers.length) {
    return (
      <div className="team-pairing">
        <h2>Team Pairing</h2>
        <p>Loading...</p>
      </div>
    );
  }

  const incompleteTeamsCount = sortedTeams.filter(isTeamIncomplete).length;
  const issueTeamsCount = sortedTeams.filter((t) => (t.notificationStatus || '') === 'issue').length;
  const teamsFiltersActive =
    teamsFilterIssue !== 'all' ||
    teamsFilterIncomplete !== 'all' ||
    teamsFilterMentorEmail !== 'all' ||
    teamsFilterTeamEmail !== 'all';

  return (
    <div className="team-pairing">
      <header className="team-pairing-header">
        <div className="team-pairing-header-top">
          <h2>Team Pairing</h2>
          <div className="team-pairing-tabs">
            <button
              type="button"
              className={`team-pairing-tab ${activeTab === 'pairing' ? 'active' : ''}`}
              onClick={() => setActiveTab('pairing')}
            >
              Pairing
            </button>
            <button
              type="button"
              className={`team-pairing-tab ${activeTab === 'teams' ? 'active' : ''}`}
              onClick={() => setActiveTab('teams')}
            >
              Teams ({teams.length})
              {issueTeamsCount > 0 && <span className="tab-badge tab-badge-issue">{issueTeamsCount}</span>}
              {incompleteTeamsCount > 0 && <span className="tab-badge tab-badge-incomplete">{incompleteTeamsCount}</span>}
            </button>
          </div>
          <div className="team-pairing-event">
            <label>Event:</label>
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
            >
              {activeEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.title}</option>
              ))}
            </select>
          </div>
        </div>
        {error && <p className="team-pairing-error">{error}</p>}
        {activeTab === 'pairing' && (
          <div className="team-pairing-toolbar">
            <span className="toolbar-section">
              <span className="toolbar-label">Mentor:</span>
              {selectedMentorEmail ? (
                <span className="toolbar-chip">
                  {flyerLabel(mentors.find((m) => m.email === selectedMentorEmail))}
                  <button type="button" className="chip-clear" onClick={() => setSelectedMentorEmail('')}>×</button>
                </span>
              ) : (
                <span className="toolbar-placeholder">—</span>
              )}
            </span>
            <span className="toolbar-section">
              <span className="toolbar-label">Freshlings:</span>
              {selectedFreshlingEmails.length > 0 ? (
                selectedFreshlingEmails.map((e) => (
                  <span key={e} className="toolbar-chip">
                    {flyerLabel(freshlings.find((f) => f.email === e))}
                    <button type="button" className="chip-clear" onClick={() => toggleFreshlingSelection(e)}>×</button>
                  </span>
                ))
              ) : (
                <span className="toolbar-placeholder">— (need 2)</span>
              )}
              {selectedFreshlingEmails.length === 1 && <span className="toolbar-hint">(need 1 more)</span>}
            </span>
            <span className="toolbar-section">
              <label className="toolbar-input-label">
                Public Team # (optional):
                <input
                  type="text"
                  value={nextTeamNumber || suggestedNextTeamNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNextTeamNumber(value);
                    const num = value.trim();
                    if (!num) {
                      setEditingTeamId(null);
                      return;
                    }
                    const existing = teams.find(
                      (t) => String(t.teamNumber || '').trim() === num
                    );
                    if (existing) {
                      setEditingTeamId(existing.id);
                      setSelectedMentorEmail(existing.mentor || '');
                      setSelectedFreshlingEmails(
                        Array.isArray(existing.freshlings)
                          ? existing.freshlings.filter(Boolean)
                          : []
                      );
                      setNewPasscode(existing.passcode || '');
                    } else {
                      setEditingTeamId(null);
                    }
                  }}
                  className="toolbar-input"
                />
              </label>
            </span>
            <button
              type="button"
              className="team-pairing-create-btn"
              onClick={handleCreateTeam}
              disabled={creating || (!selectedMentorEmail && selectedFreshlingEmails.length === 0)}
            >
              {creating ? 'Saving…' : editingTeamId ? 'Update Team' : 'Create Team'}
            </button>
          </div>
        )}
        {activeTab === 'teams' && (
          <div className="team-pairing-toolbar">
            <label className="team-pairing-search-label">
              Search: <input
                type="text"
                className="team-pairing-search-input"
                placeholder="Name or email (mentor/freshling)..."
                value={teamsViewSearch}
                onChange={(e) => setTeamsViewSearch(e.target.value)}
              />
            </label>
            <span className="toolbar-stats">
              {teamsViewSearch.trim() || teamsFiltersActive
                ? `${filteredSortedTeams.length} of ${teams.length} team(s)`
                : `${teams.length} team(s)`}
              {' · '}{assignedMentorEmails.size} mentors · {assignedFreshlingEmails.size} freshlings assigned
            </span>
            <label className="toolbar-filter-label">
              Problem:
              <select
                value={teamsFilterIssue}
                onChange={(e) => setTeamsFilterIssue(e.target.value)}
                className="toolbar-filter-select"
              >
                <option value="all">All</option>
                <option value="only">Issues only</option>
                <option value="hide">Hide issues</option>
              </select>
            </label>
            <label className="toolbar-filter-label">
              Incomplete:
              <select
                value={teamsFilterIncomplete}
                onChange={(e) => setTeamsFilterIncomplete(e.target.value)}
                className="toolbar-filter-select"
              >
                <option value="all">All</option>
                <option value="only">Incomplete only</option>
                <option value="complete">Complete only</option>
              </select>
            </label>
            <label className="toolbar-filter-label">
              Mentor email:
              <select
                value={teamsFilterMentorEmail}
                onChange={(e) => setTeamsFilterMentorEmail(e.target.value)}
                className="toolbar-filter-select"
              >
                <option value="all">All</option>
                <option value="not_sent">Not sent</option>
                <option value="sent">Sent</option>
              </select>
            </label>
            <label className="toolbar-filter-label">
              Team email:
              <select
                value={teamsFilterTeamEmail}
                onChange={(e) => setTeamsFilterTeamEmail(e.target.value)}
                className="toolbar-filter-select"
              >
                <option value="all">All</option>
                <option value="not_sent">Not sent</option>
                <option value="sent">Sent</option>
              </select>
            </label>
            <label className="toolbar-filter-label">
              Sort:
              <select
                value={teamsSortBy}
                onChange={(e) => setTeamsSortBy(e.target.value)}
                className="toolbar-filter-select"
              >
                <option value="default">Default (alerts first, then #)</option>
                <option value="teamNumber">Team #</option>
                <option value="mentor">Mentor name</option>
                <option value="status">Notification status</option>
                <option value="mentorEmail">Mentor email state</option>
                <option value="teamEmail">Team email state</option>
              </select>
            </label>
            {teamsSortBy !== 'default' && (
              <label className="toolbar-filter-label">
                Dir:
                <select
                  value={teamsSortDir}
                  onChange={(e) => setTeamsSortDir(e.target.value)}
                  className="toolbar-filter-select"
                >
                  <option value="asc">A→Z / low→high</option>
                  <option value="desc">Z→A / high→low</option>
                </select>
              </label>
            )}
            <button
              type="button"
              className="team-pairing-clean-unused-btn btn-fm-print-outline"
              onClick={() => openPairingTableExport('print')}
              disabled={!pairingTableRowRecords.length}
            >
              Print table
            </button>
            <AdminCopyMenu
              ariaLabel="Copy teams table or mentor emails"
              disabled={!teams.length && !pairingTableRowRecords.length}
              items={[
                {
                  label: 'Copy table…',
                  disabled: !pairingTableRowRecords.length,
                  onSelect: () => openPairingTableExport('copy'),
                  showCopiedFeedback: false,
                },
                {
                  label: 'Copy mentor emails',
                  disabled: !teams.length,
                  onSelect: copyAllPairedMentorEmails,
                },
              ]}
            />
            <label className="toolbar-toggle">
              <input
                type="checkbox"
                checked={teamsCompactView}
                onChange={(e) => setTeamsCompactView(e.target.checked)}
              />
              Compact view
            </label>
            <button
              type="button"
              className="team-pairing-clean-unused-btn"
              onClick={handleAssignTeamNumbersToBlanks}
              disabled={assigningTeamNumbers || !teams.length}
            >
              {assigningTeamNumbers ? 'Assigning…' : 'Assign # to blanks'}
            </button>
            <button
              type="button"
              className="team-pairing-clean-unused-btn"
              onClick={handleRemoveUnusedFromTeams}
              disabled={cleaningUnused || !teams.length}
            >
              {cleaningUnused ? 'Cleaning…' : 'Clean unused'}
            </button>
            <button
              type="button"
              className="team-pairing-clean-unused-btn"
              onClick={handleClearAllTeamNumbers}
              disabled={clearingTeamNumbers || !teams.length}
            >
              {clearingTeamNumbers ? 'Clearing…' : 'Clear all #'}
            </button>
          </div>
        )}
      </header>

      {activeTab === 'pairing' && (
      <div className="team-pairing-layout team-pairing-layout-full">
        <section className="team-pairing-unassigned">
          <div className="team-pairing-filters">
            <label className="team-pairing-search-label">
              Search: <input
                type="text"
                className="team-pairing-search-input"
                placeholder="Name or email..."
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
              />
            </label>
            <label>
              Show:{' '}
              <select value={filterHasTeammates} onChange={(e) => setFilterHasTeammates(e.target.value)}>
                <option value="all">All</option>
                <option value="yes">Has teammates</option>
                <option value="no">No teammates</option>
              </select>
            </label>
            <label>
              Sort:{' '}
              <select value={sortUnassignedBy} onChange={(e) => setSortUnassignedBy(e.target.value)}>
                <option value="teammates">Teammates first</option>
                <option value="alpha">Alphabetical</option>
                <option value="sheetOrder">Sheet order</option>
                <option value="ht">Height</option>
                <option value="wt">Weight</option>
                <option value="wt-ht">Wt → Ht</option>
                <option value="jumps">Jumps</option>
                <option value="days">Days</option>
              </select>
            </label>
          </div>
          <div className="team-pairing-cards">
            {pairingUnassignedQueuesEmpty && (
              <div className="team-pairing-all-paired-banner" role="status">
                {mentorsInPairingPoolTotal === 0 && pairableFreshlingCount === 0 ? (
                  <p className="team-pairing-empty">
                    No assignable mentors or freshlings for this event.
                  </p>
                ) : (
                  <p className="team-pairing-all-paired-message">
                    All {mentorsInPairingPoolTotal} mentor{mentorsInPairingPoolTotal === 1 ? '' : 's'} and{' '}
                    {pairableFreshlingCount} freshling{pairableFreshlingCount === 1 ? '' : 's'} are paired.
                  </p>
                )}
              </div>
            )}
            <div className="team-pairing-card-group team-pairing-mentors-col">
              <h4>
                Mentors ({filteredMentorsBySearch.length})
                {waitingMentors.length > 0 && (
                  <span className="team-pairing-waiting-label" title="On waiting list – not assignable">
                    {' '}— {waitingMentors.length} on waiting list
                  </span>
                )}
              </h4>
              {selectedMentorFlyer && (
                <div className={`mentor-detail-panel ${selectedMentorFlyer.test ? 'mentor-detail-panel--test' : ''}`}>
                  <div className="mentor-detail-title">
                    <span>{flyerLabel(selectedMentorFlyer)}</span>
                    {selectedMentorFlyer.test && <span className="test-badge">TEST</span>}
                    {selectedMentorFlyer.fsJumps && (
                      <span className="mentor-detail-badge" title="FS jumps">
                        FS {selectedMentorFlyer.fsJumps}
                      </span>
                    )}
                    {selectedMentorFlyer.ratings && (
                      <span className="mentor-detail-badge" title="Ratings">
                        {selectedMentorFlyer.ratings}
                      </span>
                    )}
                  </div>
                    <div className="mentor-detail-body">
                    <div className="mentor-detail-row">
                      <span className="label">Email</span>
                      <span className="value">{selectedMentorFlyer.email || '—'}</span>
                    </div>
                    <div className="mentor-detail-row">
                      <span className="label">Days</span>
                      <span className="value">{abbreviateDays(selectedMentorFlyer.days) || '—'}</span>
                    </div>
                    {selectedMentorFlyer.team && (
                      <div className="mentor-detail-row">
                        <span className="label">Team pref</span>
                        <span className="value">{selectedMentorFlyer.team}</span>
                      </div>
                    )}
                    {selectedMentorFlyer.notes && (
                      <div className="mentor-detail-row">
                        <span className="label">Notes</span>
                        <span className="value">{selectedMentorFlyer.notes}</span>
                      </div>
                    )}
                    <div className="mentor-detail-row">
                      <span className="label">Registration</span>
                      <span className="value">{registrationMentorStatusLabel(selectedMentorFlyer)}</span>
                    </div>
                    {mentorNeedsFuryApproveForAssignment(selectedMentorFlyer) &&
                      selectedEventObj?.registrationImportSource === 'fury_api' && (
                      <div className="mentor-detail-row mentor-detail-fury-actions">
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={furyApprovingMentor}
                          onClick={handleRequestFuryMentorApprove}
                        >
                          {furyApprovingMentor ? 'Requesting…' : 'Request mentor approve (Fury)'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="team-pairing-mentors-scroll">
                    {filteredMentorsBySearch.map((m) => (
                  <div
                    key={m.id}
                    className={`mentor-chip ${selectedMentorEmail === m.email ? 'selected' : ''} ${m.test ? 'mentor-chip--test' : ''} ${isMentorRegistrationDeclinedForPairing(m) ? 'mentor-chip--declined-reg' : !isMentorEligibleForPairing(m) ? 'mentor-chip--pending-reg' : ''}`}
                    onClick={() => setSelectedMentorEmail(selectedMentorEmail === m.email ? '' : m.email)}
                    title={`${flyerLabel(m)}${m.team ? ` | Team pref: ${m.team}` : ''}${m.fsJumps ? ` | FS jumps: ${m.fsJumps}` : ''}${m.ratings ? ` | Ratings: ${m.ratings}` : ''}${m.notes ? ` | Notes: ${m.notes}` : ''}`}
                  >
                    <div className="mentor-chip-main">
                      <span className="mentor-chip-name">
                        {flyerLabel(m)}
                        {m.test && <span className="test-badge" title="Test (dummy) data">TEST</span>}
                        {m.email && (
                          <>
                            <span className="email-copy-btn-wrap">
                              <button
                                type="button"
                                className="email-copy-btn"
                                title="Copy email"
                                aria-label="Copy email"
                                onClick={(e) => copyEmailToClipboard(e, m.email, `pair-m-${m.id}`)}
                              >
                                📧
                              </button>
                              {emailCopyFeedbackKey === `pair-m-${m.id}` && (
                                <span className="email-copy-tooltip" role="status">
                                  Copied!
                                </span>
                              )}
                            </span>
                            <button
                              type="button"
                              className="email-compose-btn"
                              title="Email mentor"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEmailComposer({
                                  template: 'mentor_warning',
                                  to: m.email || '',
                                  bcc: '',
                                  context: {
                                    type: 'mentor',
                                    flyer: m,
                                    event: selectedEventObj,
                                  },
                                });
                              }}
                            >
                              ✉
                            </button>
                          </>
                        )}
                      </span>
                      {m.team && (
                        <span className="mentor-chip-team" title="Team preference">
                          {m.team}
                        </span>
                      )}
                    </div>
                    {m.notes && (
                      <button
                        type="button"
                        className="mentor-chip-notes"
                        onClick={(e) => openNotesModal(m, e)}
                        title={m.notes}
                      >
                        📝
                      </button>
                    )}
                    {isMentorRegistrationDeclinedForPairing(m) && (
                      <span
                        className="mentor-chip-declined-badge"
                        title={
                          (m.registrationMentorStatus || '').toString().toLowerCase() === 'partial_denied'
                            ? 'Partial line denial on mentor registration'
                            : 'Staff declined mentor registration'
                        }
                      >
                        {(m.registrationMentorStatus || '').toString().toLowerCase() === 'partial_denied'
                          ? 'Partial deny'
                          : 'Declined'}
                      </span>
                    )}
                    {!isMentorEligibleForPairing(m) && !isMentorRegistrationDeclinedForPairing(m) && (
                      <span className="mentor-chip-pending-badge" title="Not staff-approved in registration yet">
                        Pending
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {waitingMentors.length > 0 && (
                <div className="team-pairing-waiting-section">
                  <div className="team-pairing-waiting-section-title">Waiting list (not assignable)</div>
                  {waitingMentors.map((m) => (
                    <div
                      key={m.id}
                      className={`mentor-chip mentor-chip--waiting ${m.test ? 'mentor-chip--test' : ''}`}
                      title={`${flyerLabel(m)} — waiting list${m.test ? ' [TEST]' : ''}`}
                    >
                      <span className="mentor-chip-name">{flyerLabel(m)}{m.test && <span className="test-badge">TEST</span>}</span>
                      {m.team && <span className="mentor-chip-team">{m.team}</span>}
                    </div>
                  ))}
                </div>
              )}
              {excludedUnassignedMentors.length > 0 && (
                <div className="team-pairing-excluded-section">
                  <div className="team-pairing-excluded-section-title">Excluded from mentor pairing (FM policy)</div>
                  {excludedUnassignedMentors.map((m) => (
                    <div
                      key={m.id}
                      className={`mentor-chip mentor-chip--excluded ${m.test ? 'mentor-chip--test' : ''}`}
                      title={m.fmMentorPairingNote || 'Not eligible for mentor pairing in Fresh Meet'}
                    >
                      <span className="mentor-chip-name">{flyerLabel(m)}</span>
                      {m.test && <span className="test-badge">TEST</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="team-pairing-card-group team-pairing-freshlings-col">
              <h4>Freshlings ({filteredSortedFreshlings.length})</h4>
              <div className="team-pairing-metrics-header">
                <span className="metrics-col-name">Name</span>
                <span>Ht</span>
                <span>Wt</span>
                <span>Age</span>
                <span>Jumps</span>
                <span>Home DZ</span>
                <span>Int</span>
                <span>Days</span>
                <span>Teammates</span>
                <span></span>
              </div>
              {selectedFreshlingEmails.length > 0 && (
                <div className="selection-compare-panel">
                  {selectedFreshlingEmails.map((email) => {
                    const f = freshlings.find((fl) => fl.email === email);
                    if (!f) return null;
                    return (
                      <div key={email} className="selection-compare-row">
                        <span className="metrics-col-name selection-name">
                          {flyerLabel(f)}
                          {f.test && <span className="test-badge">TEST</span>}
                          <button type="button" className="selection-clear" onClick={(e) => { e.stopPropagation(); toggleFreshlingSelection(email); }}>×</button>
                        </span>
                        <span>{formatHeight(f.ht) || '—'}</span>
                        <span>{formatWeight(f.wt) || '—'}</span>
                        <span>{getAgeFromYear(f.yearBorn) ?? '—'}</span>
                        <span>{f.jumpNumber || '—'}</span>
                        <span>{f.homeDz || '—'}</span>
                        <span>{f.intensity || '—'}</span>
                        <span>{abbreviateDays(f.days) || '—'}</span>
                        <span title={f.teammates || ''}>{(f.teammates || '—').toString().slice(0, 18)}</span>
                        <span></span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="team-pairing-freshlings-scroll">
                {filteredSortedFreshlings.map((f) => (
                  <div
                    key={f.id}
                    className={`team-pairing-freshling-row ${selectedFreshlingEmails.includes(f.email) ? 'selected' : ''} ${isWithdrawnOrDuplicate(f) ? 'dimmed' : ''} ${f.test ? 'freshling-row--test' : ''}`}
                    onClick={() => toggleFreshlingSelection(f.email)}
                  >
                    <span className="metrics-col-name" title={flyerLabel(f)}>
                      {flyerLabel(f)}
                      {f.test && <span className="test-badge">TEST</span>}
                      {f.email && (
                        <>
                          <span className="email-copy-btn-wrap">
                            <button
                              type="button"
                              className="email-copy-btn"
                              title="Copy email"
                              aria-label="Copy email"
                              onClick={(e) => copyEmailToClipboard(e, f.email, `pair-f-${f.id}`)}
                            >
                              📧
                            </button>
                            {emailCopyFeedbackKey === `pair-f-${f.id}` && (
                              <span className="email-copy-tooltip" role="status">
                                Copied!
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            className="email-compose-btn"
                            title="Email freshling"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEmailComposer({
                                template: 'custom',
                                to: f.email || '',
                                bcc: '',
                                context: {
                                  type: 'flyer',
                                  flyer: f,
                                  event: selectedEventObj,
                                },
                              });
                            }}
                          >
                            ✉
                          </button>
                        </>
                      )}
                    </span>
                    <span>{formatHeight(f.ht) || '—'}</span>
                    <span>{formatWeight(f.wt) || '—'}</span>
                    <span>{getAgeFromYear(f.yearBorn) ?? '—'}</span>
                    <span>{f.jumpNumber || '—'}</span>
                    <span>{f.homeDz || '—'}</span>
                    <span>{f.intensity || '—'}</span>
                    <span>{abbreviateDays(f.days) || '—'}</span>
                    <span title={f.teammates || ''}>{(f.teammates || '—').toString().slice(0, 18)}</span>
                    <span>
                      {f.notes && (
                        <button
                          type="button"
                          className="freshling-notes-icon"
                          onClick={(e) => openNotesModal(f, e)}
                          title={f.notes}
                        >
                          📝
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
      )}

      {activeTab === 'teams' && teamsCompactView && (
        <section className="team-pairing-formed">
          <div className="teams-compact-header">
            <span>Team</span>
            <span>Mentor</span>
            <span>Freshlings</span>
            <span>Status</span>
          </div>
          <div className="teams-compact-list">
            {filteredSortedTeams.map((team) => {
              const mentorFlyer = team.mentor ? mentors.find((m) => (m.email || '').toLowerCase() === (team.mentor || '').toLowerCase()) : null;
              const freshlingFlyers = (team.freshlings || []).map(
                (email) => freshlings.find((f) => (f.email || '').toLowerCase() === (email || '').toLowerCase()) || { email }
              );
              const mentorCancelled = mentorFlyer && isCancelledOrWithdrawn(mentorFlyer);
              const status = team.notificationStatus || 'pending';
              const hasWarningShot = !!(team.mentorWarningShotSentAt || team.mentorWarningShotManual);
              const hasTeamEmail = !!(team.teamIntroductionSentAt || team.teamIntroductionManual);
              const incomplete = isTeamIncomplete(team);
              const hasWithdrawn = teamHasWithdrawnOrDuplicate(team);
              const mentorPolicyIssue = teamHasMentorPairingPolicyIssue(team);
              let rowStatus = 'status-none';
              if (incomplete || status === 'issue' || hasWithdrawn || mentorPolicyIssue) {
                rowStatus = 'status-error';
              } else if (hasTeamEmail) {
                rowStatus = 'status-sent';
              } else if (hasWarningShot) {
                rowStatus = 'status-warning';
              }
              return (
                <div
                  key={team.id}
                  role="button"
                  tabIndex={0}
                  className={`teams-compact-row ${rowStatus} ${team.test ? 'teams-compact-row--test' : ''}`}
                  onClick={() => {
                    setScrollFocusTeamId(team.id);
                    setTeamsCompactView(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setScrollFocusTeamId(team.id);
                      setTeamsCompactView(false);
                    }
                  }}
                >
                  <span className="compact-team-num">
                    {getTeamDisplayLabel(teamForDisplayLabel(team, mentorFlyer))}
                    {team.test && <span className="compact-test-badge"> TEST</span>}
                  </span>
                  <span className={`compact-mentor ${mentorCancelled ? 'cancelled' : ''} ${mentorFlyer?.test ? 'compact--test' : ''}`}>
                    {mentorFlyer ? flyerLabel(mentorFlyer) : '—'}
                    {mentorCancelled && <span className="compact-cancelled-badge"> — CANCELLED</span>}
                  </span>
                  <span className="compact-freshlings">
                    {freshlingFlyers.length
                      ? freshlingFlyers.map((f, idx) => {
                          const cancelled = isCancelledOrWithdrawn(f);
                          return (
                            <span key={f.email || idx} className={`compact-freshling-name ${cancelled ? 'cancelled' : ''} ${f.test ? 'compact--test' : ''}`}>
                              {flyerLabel(f)}
                              {f.test && <span className="compact-test-badge"> TEST</span>}
                              {cancelled && <span className="compact-cancelled-badge"> CANCELLED</span>}
                              {idx < freshlingFlyers.length - 1 ? ', ' : ''}
                            </span>
                          );
                        })
                      : '—'}
                  </span>
                  <span className="compact-status">{status}</span>
                </div>
              );
            })}
          </div>
          {teams.length > 0 &&
            filteredSortedTeams.length === 0 &&
            (teamsViewSearch.trim() || teamsFiltersActive) && (
              <p className="team-pairing-empty">
                {teamsViewSearch.trim()
                  ? `No teams match "${teamsViewSearch.trim()}". Try a different name or email.`
                  : 'No teams match the current filters. Adjust Problem, Incomplete, or email filters.'}
              </p>
            )}
        </section>
      )}

      {activeTab === 'teams' && !teamsCompactView && (
        <section
          className="team-pairing-formed"
          data-detail-team-count={teamsForDetailCards.length}
        >
          <div className="team-pairing-teams-grid">
            {teamsForDetailCards.map((team) => {
              const incomplete = isTeamIncomplete(team);
              const hasWithdrawn = teamHasWithdrawnOrDuplicate(team);
              const mentorFlyer = team.mentor ? mentors.find((m) => (m.email || '').toLowerCase() === (team.mentor || '').toLowerCase()) : null;
              const mentorRegDeclined = mentorFlyer && isMentorRegistrationDeclinedForPairing(mentorFlyer);
              const mentorPolicyIssue = teamHasMentorPairingPolicyIssue(team);
              const freshlingFlyers = (team.freshlings || []).map(
                (email) => freshlings.find((f) => (f.email || '').toLowerCase() === (email || '').toLowerCase()) || { email }
              );
              const needFreshlings = 2 - freshlingFlyers.length;
              return (
              <div
                key={team.id}
                ref={(el) => {
                  if (el) teamCardRefs.current[team.id] = el;
                  else delete teamCardRefs.current[team.id];
                }}
                className={`team-detail-card ${
                  team.test ? 'team-detail-card--test' : ''
                } ${
                  highlightTeamId === team.id ? 'team-detail-card--highlight' : ''
                } ${
                  incomplete || team.notificationStatus === 'issue' || hasWithdrawn || mentorPolicyIssue
                    ? 'team-detail-card-error'
                    : !incomplete && !hasWithdrawn && !mentorPolicyIssue && (team.teamIntroductionSentAt || team.teamIntroductionManual)
                    ? 'team-detail-card-sent'
                    : !incomplete && !hasWithdrawn && !mentorPolicyIssue && (team.mentorWarningShotSentAt || team.mentorWarningShotManual)
                    ? 'team-detail-card-warning'
                    : ''
                }`}
              >
                <div className="team-detail-header">
                  <div className="team-detail-display-label">
                    {getTeamDisplayLabel(teamForDisplayLabel(team, mentorFlyer))}
                  </div>
                  <span className="team-detail-number">
                    Team #{' '}
                    <input
                      type="text"
                      defaultValue={team.teamNumber || ''}
                      className="team-number-input"
                      onBlur={(e) => handleInlineTeamNumberChange(team.id, e.target.value)}
                    />
                    {!team.teamNumber && <span className="team-number-unassigned-label">(Unassigned)</span>}
                  </span>
                  {team.test && <span className="team-detail-badge test">TEST</span>}
                  {incomplete && <span className="team-detail-badge incomplete">Incomplete</span>}
                  {hasWithdrawn && <span className="team-detail-badge withdrawn">Withdrawn</span>}
                  {mentorRegDeclined && !hasWithdrawn && (
                    <span
                      className="team-detail-badge mentor-declined-issue"
                      title="Staff declined this mentor’s registration (pairing not allowed)"
                    >
                      Mentor declined
                    </span>
                  )}
                  {mentorPolicyIssue && !mentorRegDeclined && !hasWithdrawn && (
                    <span
                      className="team-detail-badge mentor-reg-issue"
                      title="Mentor is excluded from mentor pairing in Flyer Management"
                    >
                      Pairing excluded
                    </span>
                  )}
                  <div className="team-detail-actions">
                    <button
                      type="button"
                      className="btn-sm"
                      onClick={() => {
                        const mentorEmail = (team.mentor || '').trim();
                        openEmailComposer({
                          template: 'mentor_warning',
                          to: mentorEmail,
                          bcc: '',
                          context: {
                            type: 'mentor',
                            team,
                            teamId: team.id,
                            teamNumber: team.teamNumber,
                            mentors,
                            freshlings,
                            event: selectedEventObj,
                            markMentorWarningSent: true,
                          },
                        });
                      }}
                      disabled={!team.mentor}
                    >
                      Mentor email
                    </button>
                    <button
                      type="button"
                      className="btn-sm"
                      onClick={() => {
                        const mentorEmail = (team.mentor || '').trim();
                        const freshlingEmails = (team.freshlings || [])
                          .map((e) => (e || '').trim())
                          .filter(Boolean);
                        const bcc = [mentorEmail, ...freshlingEmails].filter(Boolean).join(',');
                        const to = TEAM_TO_EMAIL ? TEAM_TO_EMAIL.trim() : '';
                        openEmailComposer({
                          template: 'team_intro',
                          to,
                          bcc,
                          context: {
                            type: 'team',
                            team,
                            teamId: team.id,
                            teamNumber: team.teamNumber,
                            mentors,
                            freshlings,
                            event: selectedEventObj,
                            markTeamIntroSent: true,
                          },
                        });
                      }}
                      disabled={!((team.freshlings || []).length)}
                    >
                      Team email
                    </button>
                    <button type="button" className="btn-sm btn-danger" onClick={() => handleRemoveTeam(team.id)}>Remove</button>
                  </div>
                </div>
                
                <div className="team-detail-section">
                  <div className="team-detail-section-label">Mentor</div>
                  {mentorFlyer ? (
                    <div
                      className={`team-detail-member mentor-member ${isCancelledOrWithdrawn(mentorFlyer) ? 'member-cancelled' : ''} ${
                        mentorRegDeclined && !isCancelledOrWithdrawn(mentorFlyer) ? 'member-mentor-declined-reg' : ''
                      } ${mentorPolicyIssue && !mentorRegDeclined && !isCancelledOrWithdrawn(mentorFlyer) ? 'member-mentor-pending-reg' : ''} ${mentorFlyer.test ? 'member--test' : ''}`}
                    >
                      <span className="member-name" title={mentorFlyer.email}>
                        {flyerLabel(mentorFlyer)}
                        {mentorFlyer.test && <span className="test-badge" title="Test (dummy) data">TEST</span>}
                        {isCancelledOrWithdrawn(mentorFlyer) && (
                          <span className="member-cancelled-badge" title="This mentor is cancelled or withdrawn">CANCELLED</span>
                        )}
                        {mentorRegDeclined && !isCancelledOrWithdrawn(mentorFlyer) && (
                          <span className="member-cancelled-badge member-declined-reg-badge" title="Staff declined mentor registration">
                            Declined
                          </span>
                        )}
                        {mentorPolicyIssue && !mentorRegDeclined && !isCancelledOrWithdrawn(mentorFlyer) && (
                          <span className="member-cancelled-badge member-pending-reg-badge" title="Excluded from mentor pairing">
                            EXCLUDED
                          </span>
                        )}
                        {mentorFlyer.email && (
                          <>
                            <span className="email-copy-btn-wrap">
                              <button
                                type="button"
                                className="email-copy-btn"
                                title="Copy email"
                                aria-label="Copy email"
                                onClick={(e) =>
                                  copyEmailToClipboard(e, mentorFlyer.email, `team-${team.id}-m`)
                                }
                              >
                                📧
                              </button>
                              {emailCopyFeedbackKey === `team-${team.id}-m` && (
                                <span className="email-copy-tooltip" role="status">
                                  Copied!
                                </span>
                              )}
                            </span>
                            <button
                              type="button"
                              className="email-compose-btn"
                              title="Email mentor"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEmailComposer({
                                  template: 'mentor_warning',
                                  to: mentorFlyer.email || '',
                                  bcc: '',
                                  context: {
                                    type: 'mentor',
                                    flyer: mentorFlyer,
                                    event: selectedEventObj,
                                  },
                                });
                              }}
                            >
                              ✉
                            </button>
                          </>
                        )}
                      </span>
                      <span className="member-meta">{abbreviateDays(mentorFlyer.days) || '—'}</span>
                      {mentorFlyer.team && <span className="member-meta" title="Team preference">Pref: {mentorFlyer.team}</span>}
                      {mentorFlyer.fsJumps && <span className="member-meta" title="FS jumps">FS {mentorFlyer.fsJumps}</span>}
                      {mentorFlyer.ratings && <span className="member-meta" title="Ratings">{mentorFlyer.ratings}</span>}
                      <button type="button" className="btn-xs btn-danger" onClick={() => handleRemoveMentorFromTeam(team.id)}>×</button>
                    </div>
                  ) : (
                    <div className="team-detail-missing">
                      {unassignedMentors.some((m) => isMentorEligibleForPairing(m)) ? (
                        <select
                          className="add-member-select"
                          value=""
                          onChange={(e) => { if (e.target.value) handleAddMentorToTeam(team.id, e.target.value); }}
                        >
                          <option value="">Add mentor…</option>
                          {unassignedMentors.filter((m) => isMentorEligibleForPairing(m)).map((m) => (
                            <option key={m.id} value={m.email}>{m.test ? '[Test] ' : ''}{flyerLabel(m)}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="missing-label">No mentor</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="team-detail-section">
                  <div className="team-detail-section-label">Freshlings ({freshlingFlyers.length}/2)</div>
                  <div className="team-detail-freshlings-table">
                    <div className="freshling-table-header">
                      <span>Name</span>
                      <span>Ht</span>
                      <span>Wt</span>
                      <span>Jumps</span>
                      <span>Home DZ</span>
                      <span>Days</span>
                      <span>Teammates</span>
                      <span></span>
                    </div>
                    {freshlingFlyers.map((f) => {
                      const flCopyKey = `team-${team.id}-f-${(f.email || '').toLowerCase()}`;
                      return (
                      <div key={f.email} className={`freshling-table-row ${isCancelledOrWithdrawn(f) ? 'member-cancelled' : ''} ${f.test ? 'member--test' : ''}`}>
                        <span className="member-name" title={f.email}>
                          {flyerLabel(f)}
                          {f.test && <span className="test-badge" title="Test (dummy) data">TEST</span>}
                          {isCancelledOrWithdrawn(f) && (
                            <span className="member-cancelled-badge" title="This freshling is cancelled or withdrawn">CANCELLED</span>
                          )}
                          {f.email && (
                            <>
                              <span className="email-copy-btn-wrap">
                                <button
                                  type="button"
                                  className="email-copy-btn"
                                  title="Copy email"
                                  aria-label="Copy email"
                                  onClick={(e) => copyEmailToClipboard(e, f.email, flCopyKey)}
                                >
                                  📧
                                </button>
                                {emailCopyFeedbackKey === flCopyKey && (
                                  <span className="email-copy-tooltip" role="status">
                                    Copied!
                                  </span>
                                )}
                              </span>
                              <button
                                type="button"
                                className="email-compose-btn"
                                title="Email freshling"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEmailComposer({
                                    template: 'custom',
                                    to: f.email || '',
                                    bcc: '',
                                    context: {
                                      type: 'flyer',
                                      flyer: f,
                                      event: selectedEventObj,
                                    },
                                  });
                                }}
                              >
                                ✉
                              </button>
                            </>
                          )}
                        </span>
                        <span>{formatHeight(f.ht) || '—'}</span>
                        <span>{formatWeight(f.wt) || '—'}</span>
                        <span>{f.jumpNumber || '—'}</span>
                        <span>{f.homeDz || '—'}</span>
                        <span>{abbreviateDays(f.days) || '—'}</span>
                        <span title={f.teammates || ''}>{(f.teammates || '—').toString().slice(0, 15)}</span>
                        <span>
                          <button type="button" className="btn-xs btn-danger" onClick={() => handleRemoveFreshlingFromTeam(team.id, f.email)}>×</button>
                        </span>
                      </div>
                      );
                    })}
                    {needFreshlings > 0 && (
                      <div className="team-detail-add-row">
                        {unassignedFreshlings.length > 0 ? (
                          <select
                            className="add-member-select"
                            value=""
                            onChange={(e) => { if (e.target.value) handleAddFreshlingToTeam(team.id, e.target.value); }}
                          >
                            <option value="">Add freshling… ({needFreshlings} needed)</option>
                            {unassignedFreshlings.map((f) => (
                              <option key={f.id} value={f.email}>{f.test ? '[Test] ' : ''}{flyerLabel(f)} | {formatHeight(f.ht)} | {formatWeight(f.wt)} | {abbreviateDays(f.days)}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="missing-label">Need {needFreshlings} more (none available)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="team-detail-footer">
                  <label className="footer-item">
                    Status:
                    <select
                      value={team.notificationStatus || 'pending'}
                      onChange={(e) => handleNotificationStatusChange(team.id, e.target.value)}
                    >
                      {NOTIFICATION_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </label>
                  <span className="footer-item">
                    Mentor email:{' '}
                    {team.mentorWarningShotSentAt
                      ? (() => {
                          const d = team.mentorWarningShotSentAt?.toDate
                            ? team.mentorWarningShotSentAt.toDate()
                            : team.mentorWarningShotSentAt instanceof Date
                              ? team.mentorWarningShotSentAt
                              : null;
                          return d ? d.toLocaleDateString(undefined, { dateStyle: 'short' }) : '—';
                        })()
                      : team.mentorWarningShotManual
                        ? 'Marked sent (manual)'
                        : <span className="not-sent">Not sent</span>}
                    <button
                      type="button"
                      className={`btn-xs email-toggle ${team.mentorWarningShotManual ? 'on' : ''}`}
                      onClick={() => toggleMentorWarningShotManual(team)}
                      aria-label={team.mentorWarningShotManual ? 'Unmark mentor email sent' : 'Mark mentor email sent manually'}
                    >
                      ✓
                    </button>
                  </span>
                  <span className="footer-item">
                    Team email:{' '}
                    {team.teamIntroductionSentAt
                      ? (() => {
                          const d = team.teamIntroductionSentAt?.toDate
                            ? team.teamIntroductionSentAt.toDate()
                            : team.teamIntroductionSentAt instanceof Date
                              ? team.teamIntroductionSentAt
                              : null;
                          return d ? d.toLocaleDateString(undefined, { dateStyle: 'short' }) : '—';
                        })()
                      : team.teamIntroductionManual
                        ? 'Marked sent (manual)'
                        : <span className="not-sent">Not sent</span>}
                    <button
                      type="button"
                      className={`btn-xs email-toggle ${team.teamIntroductionManual ? 'on' : ''}`}
                      onClick={() => toggleTeamIntroductionManual(team)}
                      aria-label={team.teamIntroductionManual ? 'Unmark team email sent' : 'Mark team email sent manually'}
                    >
                      ✓
                    </button>
                  </span>
                </div>
              </div>
            ); })}
          </div>
          {teams.length === 0 && <p className="team-pairing-empty">No teams yet. Switch to Pairing tab to create teams.</p>}
          {teams.length > 0 &&
            filteredSortedTeams.length === 0 &&
            (teamsViewSearch.trim() || teamsFiltersActive) && (
              <p className="team-pairing-empty">
                {teamsViewSearch.trim()
                  ? `No teams match "${teamsViewSearch.trim()}". Try a different name or email.`
                  : 'No teams match the current filters. Adjust Problem, Incomplete, or email filters.'}
              </p>
            )}
        </section>
      )}

      {notesModalFlyer && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setNotesModalFlyer(null)}>
          <div
            className="fm-modal fm-modal--sm team-pairing-notes-fm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-pairing-notes-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="team-pairing-notes-title">
                Notes — {flyerLabel(notesModalFlyer)}
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setNotesModalFlyer(null)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
              <p className="team-pairing-notes-hint">Kept when you re-import from the sheet.</p>
              <textarea
                rows={4}
                value={notesEditValue}
                onChange={(e) => setNotesEditValue(e.target.value)}
                placeholder="Team changes, thoughts…"
              />
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setNotesModalFlyer(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-primary"
                onClick={saveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pairingTableExportMode && (
        <AdminColumnExportModal
          mode={pairingTableExportMode}
          columns={TEAM_PAIRING_EXPORT_COLS}
          selectedIds={pairingExportCols}
          onChangeSelected={setPairingExportCols}
          onCancel={() => setPairingTableExportMode(null)}
          onConfirm={runPairingTableExport}
          onResetDefaults={resetPairingExportCols}
        />
      )}

      <EmailComposerModal
        isOpen={emailComposerProps.isOpen}
        context={emailComposerProps.context}
        initialRecipients={emailComposerProps.initialRecipients}
        initialTemplate={emailComposerProps.initialTemplate}
        initialSubject={emailComposerProps.initialSubject}
        initialBody={emailComposerProps.initialBody}
        onClose={closeEmailComposer}
        onEmailSent={handleEmailSent}
      />
    </div>
  );
}

export default TeamPairing;
